import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';
import { UseFormResetField } from 'react-hook-form/dist/types/form';

import { DefaultOptionsInterface } from '@components/common';
import { generateDefaultValues as generateDefaultValuesKpi } from '@components/common/GroupSelectKpi/groupSelectUtils';

import { prepareKpiIdsData } from '@components/Kpi/Parameters/ParametersKpiSelector/TreeGroupSelectWrapper/TreeGroupSelect/utils';
import { changeOptionsByVendorKpi } from '@components/Kpi/Parameters/utils';

import { IFlatMoTreeNode } from '@interfaces/components/tree';
import {
	IGroupSelectOptions as GroupSelectOptionsInterfaceKpi,
	IGroupSelectOptionsKpi,
} from '@components/common/GroupSelectKpi/GroupSelect.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { TVendorsValues } from '@enums/common';
import { EFormFields } from '@enums/kpi-api';

interface UseKpiSelectorsLogicProps {
	kpiGroupData: IFlatMoTreeNode[];
	countersGroupData: IFlatMoTreeNode[];
	setValue: UseFormSetValue<FieldValues>;
	resetField: UseFormResetField<FieldValues>;
	setIsDirty: Dispatch<SetStateAction<boolean>>;
	vendor: TSelectOptionType<TVendorsValues>;
	optionsLimenetKpi?: Array<GroupSelectOptionsInterfaceKpi>;
	optionsNokiaKpi?: Array<GroupSelectOptionsInterfaceKpi>;
	maxSelection?: number | null;
}
//reuse for Parameters and Presets
export const useKpiSelectorsLogic = ({
	kpiGroupData,
	countersGroupData,
	setValue,
	resetField,
	setIsDirty,
	vendor,
	optionsLimenetKpi = [],
	optionsNokiaKpi = [],
	maxSelection,
}: UseKpiSelectorsLogicProps) => {
	const [defaultValueKpi, setDefaultValueKpi] = useState<Array<DefaultOptionsInterface>>([]);
	const [treeKpi, setTreeKpi] = useState<IFlatMoTreeNode[]>(kpiGroupData);
	const [treeCounters, setTreeCounters] = useState<IFlatMoTreeNode[]>(countersGroupData);

	const [selectedKpiNumber, setSelectedKpiNumber] = useState(0);
	const [selectedCountersNumber, setSelectedCountersNumber] = useState(0);

	const setInitCountersData = useCallback((nodes: IFlatMoTreeNode[]) => {
		setValue(EFormFields.initTreeCountersData, nodes);
	}, [setValue]);

	const setInitKpiData = useCallback((nodes: IFlatMoTreeNode[]) => {
		setValue(EFormFields.initTreeKpiData, nodes);
	}, [setValue]);

	const setTreeKpiIds = useCallback((ids: string[]) => setValue(EFormFields.treeKpiIds, ids), [setValue]);
	const setTreeCountersIds = useCallback((ids: string[]) => setValue(EFormFields.treeCountersIds, ids), [setValue]);

	useEffect(() => {
		if (kpiGroupData.length) {
			setTreeKpi(kpiGroupData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kpiGroupData.length]);

	useEffect(() => {
		if (countersGroupData.length) {
			setTreeCounters(countersGroupData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [countersGroupData.length]);

	const onChangeKpi = useCallback(
		(values: Array<IGroupSelectOptionsKpi>, isUpdateDirty: boolean | undefined = true) => {
			setValue(EFormFields.kpi, values);
			setDefaultValueKpi(generateDefaultValuesKpi(values as never));

			setIsDirty(isUpdateDirty);
		}, [setIsDirty, setValue]);

	const resetKpi = useCallback(() => {
		setDefaultValueKpi([]);
		resetField(EFormFields.kpi);
	}, [resetField]);

	const handleUpdateTreeKpi = useCallback((data: IFlatMoTreeNode[]) => {
		setTreeKpi(data);
		setTreeKpiIds(prepareKpiIdsData(data));
		setInitKpiData(data);
		setIsDirty(true);
	}, [setInitKpiData, setIsDirty, setTreeKpiIds]);

	const handleUpdateTreeCounters = useCallback((data: IFlatMoTreeNode[]) => {
		setTreeCounters(data);
		setTreeCountersIds(prepareKpiIdsData(data));
		setInitCountersData(data);
		setIsDirty(true);
	}, [setInitCountersData, setIsDirty, setTreeCountersIds]);

	const optionsKpi: IGroupSelectOptionsKpi[] = useMemo(() => {
		return changeOptionsByVendorKpi({
			vendor,
			optionsLimenet: optionsLimenetKpi,
			optionsNokia: optionsNokiaKpi,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendor?.value, optionsLimenetKpi.length, optionsNokiaKpi.length]);

	const isMaxSelectionLimitErrorMessageShown = useMemo(
		() => !!maxSelection && selectedKpiNumber > maxSelection,
		[selectedKpiNumber, maxSelection],
	);

	const isMaxSelectionLimitErrorMessageCountersShown = useMemo(
		() => !!maxSelection && selectedCountersNumber > maxSelection,
		[selectedCountersNumber, maxSelection],
	);

	const maxSelectionText = useMemo(
		() => (maxSelection ? `Max ${maxSelection} item(s) can be selected` : undefined),
		[maxSelection],
	);

	return {
		defaultValueKpi,
		treeKpi,
		treeCounters,
		optionsKpi,
		selectedKpiNumber,
		selectedCountersNumber,
		maxSelectionText,
		isMaxSelectionLimitErrorMessageShown,
		isMaxSelectionLimitErrorMessageCountersShown,
		onChangeKpi,
		resetKpi,
		handleUpdateTreeKpi,
		handleUpdateTreeCounters,
		setTreeKpi,
		setTreeCounters,
		setTreeKpiIds,
		setTreeCountersIds,
		setSelectedKpiNumber,
		setSelectedCountersNumber,
		setDefaultValueKpi,
		setInitKpiData,
		setInitCountersData,
	};
};
